/**
 * `/connect?code=XXXX-XXXX` — een app (MCP-server, desktop, iPad) vraagt om
 * namens de ingelogde gebruiker te werken. De gebruiker ziet wie het vraagt en
 * met welk niveau, kan dat niveau verlagen of tot één workspace beperken, en
 * klikt Toestaan of Weigeren. Het token zelf komt hier nooit langs: de app
 * haalt het op met z'n eigen `device_code`.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError, type ApiTokenScope, api, type DeviceApprovalDTO } from "../api/client";
import { useSession } from "../state/session";

const SCOPES: ApiTokenScope[] = ["read", "model", "admin"];

type Phase = "enter" | "loading" | "ask" | "approved" | "denied";

export function ConnectPage() {
  const { t } = useTranslation();
  const me = useSession((s) => s.me);
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") ?? "");
  const [request, setRequest] = useState<DeviceApprovalDTO | null>(null);
  const [scope, setScope] = useState<ApiTokenScope>("model");
  const [workspaceId, setWorkspaceId] = useState<string>("");
  // Standaard aan: een nieuwe AI-koppeling schrijft alleen op eigen AI-plateaus.
  const [aiPlateauOnly, setAiPlateauOnly] = useState(true);
  const [phase, setPhase] = useState<Phase>(params.get("code") ? "loading" : "enter");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (value: string) => {
    setError(null);
    setPhase("loading");
    try {
      const req = await api.getDeviceApproval(value.trim());
      setRequest(req);
      setScope(req.requested_scope);
      setWorkspaceId(req.workspace_id ?? "");
      setPhase("ask");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      setPhase("enter");
    }
  };

  useEffect(() => {
    const initial = params.get("code");
    if (initial) void lookup(initial);
  }, []);

  const decide = async (approve: boolean) => {
    if (!request) return;
    setBusy(true);
    setError(null);
    try {
      if (approve) {
        await api.approveDevice(request.user_code, {
          scope,
          workspace_id: workspaceId || null,
          ...(request.is_ai ? { ai_plateau_only: aiPlateauOnly } : {}),
        });
        setPhase("approved");
      } else {
        await api.denyDevice(request.user_code);
        setPhase("denied");
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const allowedScopes = request
    ? SCOPES.slice(0, SCOPES.indexOf(request.requested_scope) + 1)
    : SCOPES;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-300 bg-white p-6 shadow-md">
        <h1 className="mb-4 font-semibold text-lg text-slate-900">{t("connect.title")}</h1>

        {error && (
          <div className="mb-3 rounded border border-rose-300 bg-rose-50 px-3 py-2 text-rose-800 text-sm">
            {error}
          </div>
        )}

        {phase === "enter" && (
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              void lookup(code);
            }}
          >
            <label className="block text-slate-700 text-sm">
              {t("connect.enterCode")}
              <input
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1.5 text-center font-mono text-base uppercase tracking-widest focus:border-blue-500 focus:outline-none"
                value={code}
                maxLength={9}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
            <button
              type="submit"
              disabled={code.replace(/[^a-z]/gi, "").length !== 8}
              className="rounded bg-blue-600 px-3 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {t("connect.lookup")}
            </button>
          </form>
        )}

        {phase === "loading" && <p className="text-slate-500 text-sm">{t("connect.loading")}</p>}

        {phase === "ask" && request && (
          <div className="flex flex-col gap-4">
            <p className="text-slate-800 text-sm">
              {t("connect.asks", { client: request.client_name, email: me?.email ?? "" })}
            </p>
            <div>
              <div className="text-slate-500 text-xs">{t("connect.codeLabel")}</div>
              <div className="font-mono text-2xl text-slate-900 tracking-widest">
                {request.user_code}
              </div>
              <div className="text-slate-500 text-xs">{t("connect.codeHelp")}</div>
            </div>
            <fieldset className="flex flex-col gap-1 text-sm">
              <legend className="mb-1 text-slate-700">{t("connect.scopeLabel")}</legend>
              {allowedScopes.map((s) => (
                <label key={s} className="flex items-start gap-2">
                  <input
                    type="radio"
                    name="connect-scope"
                    className="mt-1"
                    checked={scope === s}
                    onChange={() => setScope(s)}
                    disabled={busy}
                  />
                  <span>
                    <span className="font-medium text-slate-800">{t(`apiTokens.scopes.${s}`)}</span>
                    <span className="block text-slate-500 text-xs">
                      {t(`apiTokens.scopeHelp.${s}`)}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-slate-700">{t("connect.workspaceLabel")}</span>
              <select
                className="rounded border border-slate-300 bg-white px-3 py-2 text-sm"
                value={workspaceId}
                onChange={(e) => setWorkspaceId(e.target.value)}
                disabled={busy}
              >
                <option value="">{t("connect.anyWorkspace")}</option>
                {me?.workspaces.map((w) => (
                  <option key={w.workspace_id} value={w.workspace_id}>
                    {w.workspace_name}
                  </option>
                ))}
              </select>
            </label>
            {request.is_ai && (
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={aiPlateauOnly}
                  onChange={(e) => setAiPlateauOnly(e.target.checked)}
                  disabled={busy}
                />
                <span>
                  <span className="font-medium text-slate-800">{t("connect.aiPlateauOnly")}</span>
                  <span className="block text-slate-500 text-xs">
                    {t("connect.aiPlateauOnlyHelp")}
                  </span>
                </span>
              </label>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => decide(true)}
                disabled={busy}
                className="flex-1 rounded bg-blue-600 px-3 py-2 font-medium text-sm text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {t("connect.approve")}
              </button>
              <button
                type="button"
                onClick={() => decide(false)}
                disabled={busy}
                className="flex-1 rounded border border-slate-300 bg-white px-3 py-2 font-medium text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
              >
                {t("connect.deny")}
              </button>
            </div>
          </div>
        )}

        {(phase === "approved" || phase === "denied") && (
          <div className="flex flex-col gap-3">
            <h2
              className={`font-semibold ${phase === "approved" ? "text-emerald-700" : "text-slate-800"}`}
            >
              {phase === "approved" ? t("connect.approvedTitle") : t("connect.deniedTitle")}
            </h2>
            <p className="text-slate-700 text-sm">
              {phase === "approved" ? t("connect.approvedBody") : t("connect.deniedBody")}
            </p>
            <p className="text-slate-500 text-xs">{t("connect.manageTokens")}</p>
            <Link to="/" className="text-blue-700 text-sm underline">
              {t("connect.backToEditor")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
